import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Tooltip } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Mirrors `SessionImportButton`'s
 * spacing so the two icons swap cleanly in the same NavigationBar slot.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Core
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  icon: {
    marginRight: theme.spacing(1),
  },
}));

/**
 * @function SessionExportButton
 * @description Standardized "export test session" affordance shown in NavigationBar, right next
 * to where the import icon sits, but only while a module's own result page has registered an
 * export handler (see `useRegisterSessionExport`) — i.e. only on that module's result page.
 * Unlike import, building the export JSON is a cheap synchronous read of already-available state,
 * so there's no loading/disabled state to manage here.
 * @param {Object} props
 * @param {Function} props.onExport - Called on click; builds and downloads the session JSON.
 * @see {@link useRegisterSessionExport}
 * **Called by:
 * @see {NavigationBar}
 * @memberof module:Core
 * @returns {JSX.Element}
 */
const SessionExportButton = ({ onExport }) => {
  const classes = useStyles();
  const [t] = useTranslation();

  return (
    <Tooltip title={t('forms.acquisition.sessionExport.button')}>
      <IconButton size="medium" className={classes.icon} onClick={onExport}>
        <GetAppIcon fontSize="inherit" color="primary" />
      </IconButton>
    </Tooltip>
  );
};

export default SessionExportButton;
